import {
  getLocalizedImageEntry,
  IMAGE_FALLBACK_LOCALES,
} from "../i18n/localizedImages";

/**
 * Normalize i18n language codes (`en-US` → `en`).
 * @param {string | undefined | null} language
 * @param {string} [fallback="en"]
 * @returns {string}
 */
export function normalizeAppLanguage(language, fallback = "en") {
  const base = String(language || fallback)
    .split("-")[0]
    .toLowerCase();
  return base || fallback;
}

/**
 * Build ordered unique fallback URLs for an image key + language.
 * Order: active locale → en → tr → any remaining registered paths.
 *
 * @param {string} imageKey
 * @param {string | undefined | null} language
 * @returns {string[]}
 */
export function getLocalizedImageFallbackChain(imageKey, language) {
  const entry = getLocalizedImageEntry(imageKey);
  if (!entry || typeof entry !== "object") return [];

  const active = normalizeAppLanguage(language);
  const preferred = [active, ...IMAGE_FALLBACK_LOCALES];
  const seen = new Set();
  const chain = [];

  for (const locale of preferred) {
    const src = entry[locale];
    if (src && !seen.has(src)) {
      seen.add(src);
      chain.push(src);
    }
  }

  for (const src of Object.values(entry)) {
    if (src && !seen.has(src)) {
      seen.add(src);
      chain.push(src);
    }
  }

  return chain;
}

/**
 * Resolve the best available image src for the active language.
 * Never returns a missing key as empty without exhausting fallbacks.
 *
 * @param {string} imageKey
 * @param {string | undefined | null} language
 * @returns {string | undefined}
 */
export function resolveLocalizedImageSrc(imageKey, language) {
  const chain = getLocalizedImageFallbackChain(imageKey, language);
  return chain[0];
}

/**
 * Resolve from an inline sources map (ad-hoc) with the same fallback rules.
 *
 * @param {Record<string, string> | undefined | null} sources
 * @param {string | undefined | null} language
 * @returns {string | undefined}
 */
export function resolveLocalizedSourcesMap(sources, language) {
  if (!sources || typeof sources !== "object") return undefined;

  const active = normalizeAppLanguage(language);
  const preferred = [active, ...IMAGE_FALLBACK_LOCALES];
  const seen = new Set();

  for (const locale of preferred) {
    const src = sources[locale];
    if (src && !seen.has(src)) return src;
    if (src) seen.add(src);
  }

  for (const src of Object.values(sources)) {
    if (src) return src;
  }

  return undefined;
}
